const max_size = 200 * 1024 * 1024;
import imageCompression from "browser-image-compression";
import { Field } from "formik";
import { ChangeEvent, FC, useRef, useState } from "react";
import {
  FiImage,
  FiUpload,
  FiX,
  FiPlus,
  FiVideo,
  FiLoader,
  FiFile,
  FiExternalLink,
} from "react-icons/fi";
import ErrorMessage from "../ErrorMessage";
import { StyleFormWrapper, StyleLabel } from "../style";
import * as S from "./style";
import useConfig from "@hooks/useConfig";

interface UploadProps {
  variant?: "banner" | "profile";
  name: string;
  placeholder?: string;
  onChange?: (value: any) => void;
  label?: string;
  styleLabel?: string;
  styleWrapper?: string;
  accept?: string;
  size?: number;
  disabled?: boolean;
  fit?: string;
  isOgImage?: boolean;
  multiple?: boolean;
  maxFiles?: number;
  height?: number;
  useRawFile?: boolean;
  onRemoveImage?: (val) => void;
  mode?: string;
  video?: boolean;
}

const Upload: FC<UploadProps> = ({
  variant = "banner",
  name,
  label,
  placeholder,
  onChange,
  styleLabel = "",
  styleWrapper = "",
  size,
  fit,
  isOgImage,
  multiple = false,
  maxFiles = 5,
  height,
  useRawFile = false,
  onRemoveImage,
  mode = "update",
  video = false,
  ...restProps
}) => {
  const { bucketUrl } = useConfig();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>("");

  const compressImage = async (file: File) => {
    if (!file.type.startsWith("image/")) return file;
    return await imageCompression(file, {
      maxSizeMB: 3,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    });
  };

  const validateFileSize = (file: File) => {
    const isVideoFile = video || file.type.startsWith("video/");

    if (isVideoFile && file.size > max_size) {
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeInMB = (max_size / (1024 * 1024)).toFixed(2);
      throw new Error(
        `Video size ${fileSizeInMB}MB exceeds maximum allowed size of ${maxSizeInMB}MB`,
      );
    }
    return true;
  };

  const getFileDetails = (file: File, base64String: string) => {
    const [info, base64] = base64String.split(",");
    const isVideoFile = video || file.type.startsWith("video/");
    const isPdf = file.type === "application/pdf";

    return {
      base64: base64?.trim() || "",
      extension: file.name.split(".").pop()?.toLowerCase() || "",
      info,
      fileName: file.name,
      rawFile: file,
      previewUrl: URL.createObjectURL(file), // Only useful for image/video/pdf to some extent
      type: isVideoFile ? "video" : isPdf ? "pdf" : "image",
    };
  };

  const handleFileChange = async (
    form: any,
    files: FileList | null,
    existingValue: any,
  ) => {
    if (!files) return;

    setIsProcessing(true);
    setError("");

    try {
      const newFiles: any[] = [];

      for (const file of Array.from(files)) {
        validateFileSize(file);

        const processedFile = file.type.startsWith("image/")
          ? await compressImage(file)
          : file;

        const reader = new FileReader();
        await new Promise<void>((resolve) => {
          reader.onload = (e: ProgressEvent<FileReader>) => {
            const fileData = getFileDetails(file, e.target?.result as string);
            newFiles.push(fileData);
            resolve();
          };
          reader.readAsDataURL(processedFile);
        });
      }

      if (multiple) {
        const updated = [...(existingValue || []), ...newFiles].slice(
          0,
          maxFiles,
        );
        onChange ? onChange(updated) : form.setFieldValue(name, updated);
        setActiveIndex(0); // Set to first image when new files are added
      } else {
        onChange
          ? onChange(newFiles[0])
          : form.setFieldValue(name, newFiles[0]);
      }
    } catch (error: any) {
      console.error("Error processing files:", error);
      setError(error.message || "Failed to upload file");
    } finally {
      setIsProcessing(false);
    }
  };

  const removeImage = (form: any, value: any[], index: number) => {
    const imageToRemove = value[index];

    const updated = value.filter((_, i) => i !== index);
    onChange ? onChange(updated) : form.setFieldValue(name, updated);

    if (activeIndex === index) {
      setActiveIndex(updated.length > 0 ? 0 : 0);
    } else if (index < activeIndex) {
      setActiveIndex((prev) => prev - 1);
    }

    if (mode === "update" && onRemoveImage && imageToRemove?.id) {
      onRemoveImage(imageToRemove);
    }
  };

  const handleThumbnailClick = (index: number) => {
    setActiveIndex(index);
  };

  const renderImageSrc = (img: any) => {
    if (img?.image) {
      return `${bucketUrl}/${img.image}`;
    }
    if (img?.videoUrl) {
      return `${bucketUrl}/${img.videoUrl}`;
    }
    if (img?.url) {
      return `${bucketUrl}/${img.url}`;
    }
    if (!img) return "";
    if (useRawFile && img.previewUrl) return img.previewUrl;
    if (img.info && img.base64) return `${img.info},${img.base64}`;
    if (typeof img === "string")
      return img.startsWith("http") ? img : `${bucketUrl}/${img}`;
    return "";
  };

  const renderPlaceholderIcon = () => {
    if (isProcessing) {
      return <FiLoader size={24} className="spin" />;
    }

    switch (variant) {
      case "banner":
        return video ? (
          <FiVideo size={24} color="#64748B" />
        ) : (
          <FiImage size={24} color="#64748B" />
        );
      case "profile":
        return <FiUpload size={16} color="white" />;
      default:
        return <FiUpload size={16} color="#64748B" />;
    }
  };

  const renderMediaPreview = (media: any) => {
    const isVideo =
      video ||
      media?.type === "video" ||
      media?.mediaType === "video" ||
      media?.videoUrl ||
      media?.url?.match(/\.(mp4|webm|ogg|mov|avi)$/i) ||
      media?.image?.match(/\.(mp4|webm|ogg|mov|avi)$/i);
    const isPdf =
      media?.type === "pdf" ||
      media?.url?.match(/\.pdf$/i) ||
      (typeof media === "string" && media.match(/\.pdf$/i));

    if (isVideo) {
      const videoSrc = renderImageSrc(media);
      return (
        <S.StyleVideoWrapper isOgImage={isOgImage} fit={fit} size={size}>
          <video
            key={videoSrc}
            controls
            preload="auto"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: variant === "profile" ? "50%" : "8px",
            }}
            src={videoSrc}
          >
            Your browser does not support the video tag.
          </video>
        </S.StyleVideoWrapper>
      );
    }

    if (isPdf) {
      const pdfUrl = renderImageSrc(media);
      return (
        <S.StyleDragImageWrapper
          isOgImage={isOgImage}
          fit={fit}
          size={size}
          as="div"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#f8fafc",
            color: "#64748b",
            position: "relative",
          }}
        >
          <FiFile size={32} />
          <span
            style={{
              fontSize: 10,
              marginTop: 4,
              textAlign: "center",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              padding: "0 8px",
            }}
          >
            {media.fileName ||
              (typeof media === "string" ? media.split("/").pop() : "PDF File")}
          </span>
          {pdfUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.open(pdfUrl, "_blank");
              }}
              style={{
                marginTop: 8,
                cursor: "pointer",
                color: "#3b82f6",
                fontSize: 11,
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "none",
                border: "none",
                padding: "4px 8px",
                zIndex: 10,
              }}
            >
              <FiExternalLink size={12} /> View PDF
            </button>
          )}
        </S.StyleDragImageWrapper>
      );
    }

    return (
      <S.StyleDragImageWrapper
        isOgImage={isOgImage}
        fit={fit}
        size={size}
        src={renderImageSrc(media)}
      />
    );
  };

  const renderThumbnailMedia = (media: any, index: number) => {
    const isVideo =
      video ||
      media?.type === "video" ||
      media?.videoUrl ||
      media?.url?.match(/\.(mp4|webm|ogg|mov|avi)$/i);
    const isPdf =
      media?.type === "pdf" ||
      media?.url?.match(/\.pdf$/i) ||
      (typeof media === "string" && media.match(/\.pdf$/i));

    if (isVideo) {
      const videoSrc = renderImageSrc(media);
      return (
        <S.StyleThumbnailVideoWrapper>
          <video
            key={videoSrc}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "4px",
            }}
            src={videoSrc}
          />
          <div className="video-indicator">
            <FiVideo size={12} />
          </div>
        </S.StyleThumbnailVideoWrapper>
      );
    }

    if (isPdf) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f1f5f9",
            borderRadius: 4,
          }}
        >
          <FiFile size={20} color="#64748b" />
        </div>
      );
    }

    return (
      <img
        src={renderImageSrc(media)}
        alt={media?.fileName || `image-${index}`}
      />
    );
  };

  const getAcceptType = () => {
    if (video) return "video/mp4,video/webm,video/ogg,video/quicktime,video/*";
    if (restProps.accept) return restProps.accept;
    return "image/*";
  };

  return (
    <Field name={name}>
      {({ form, field, meta }) => {
        const hasError = meta.touched && meta.error;
        const value = multiple ? field.value || [] : field.value || null;
        const handleClick = () => {
          if (!isProcessing && !restProps.disabled) {
            uploadInputRef.current?.click();
          }
        };

        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            {label && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
                {isProcessing && (
                  <span
                    style={{
                      marginLeft: "8px",
                      color: "#64748B",
                      fontSize: "12px",
                    }}
                  >
                    (Processing...)
                  </span>
                )}
              </StyleLabel>
            )}

            {/* Banner Upload */}
            {variant === "banner" && (
              <div style={{ display: "grid", placeItems: "center" }}>
                <S.StyleBannerUploadWrapper
                  size={size}
                  height={height}
                  isOgImage={isOgImage}
                  onClick={handleClick}
                  disabled={isProcessing || restProps.disabled}
                >
                  {isProcessing ? (
                    <S.StyleBannerUploadPlaceholder>
                      {renderPlaceholderIcon()}
                      <span>Processing...</span>
                    </S.StyleBannerUploadPlaceholder>
                  ) : !multiple ? (
                    value ? (
                      renderMediaPreview(value)
                    ) : (
                      <S.StyleBannerUploadPlaceholder>
                        {renderPlaceholderIcon()}
                        <span>
                          {placeholder ||
                            (video ? "Upload video" : "Upload image")}
                        </span>
                      </S.StyleBannerUploadPlaceholder>
                    )
                  ) : value?.length > 0 ? (
                    // Show the active image in the large preview
                    renderMediaPreview(value[activeIndex])
                  ) : (
                    <S.StyleBannerUploadPlaceholder>
                      {renderPlaceholderIcon()}
                      <span>
                        {placeholder ||
                          (video ? "Upload videos" : "Upload images")}
                      </span>
                    </S.StyleBannerUploadPlaceholder>
                  )}
                </S.StyleBannerUploadWrapper>
              </div>
            )}

            {/* Profile Upload */}
            {variant === "profile" && (
              <div style={{ display: "grid", placeItems: "center" }}>
                <S.StyleProfileUploadWrapper
                  size={size || 120}
                  onClick={handleClick}
                  title={video ? "Upload Video" : "Upload Profile"}
                  disabled={isProcessing || restProps.disabled}
                >
                  {isProcessing ? (
                    <S.StyleProfilePlaceholder>
                      <FiLoader size={18} className="spin" />
                      <span>Processing...</span>
                    </S.StyleProfilePlaceholder>
                  ) : value ? (
                    video ? (
                      <S.StyleVideoWrapper fit={fit} size={size || 120}>
                        <video
                          key={renderImageSrc(value)}
                          controls
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                          src={renderImageSrc(value)}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </S.StyleVideoWrapper>
                    ) : (
                      <S.StyleProfileImageWrapper
                        src={renderImageSrc(value)}
                        alt="Profile"
                      />
                    )
                  ) : (
                    <S.StyleProfilePlaceholder>
                      <FiUpload size={18} />
                      <span>{placeholder || "Upload"}</span>
                    </S.StyleProfilePlaceholder>
                  )}
                </S.StyleProfileUploadWrapper>
              </div>
            )}

            {/* Thumbnails */}
            {multiple && value?.length > 0 && !isProcessing && (
              <S.StyleThumbnailWrapper>
                {value.map((media: any, index: number) => (
                  <S.StyleThumbnail
                    key={index}
                    size={size}
                    isActive={index === activeIndex}
                    onClick={() => handleThumbnailClick(index)}
                    style={{ cursor: "pointer" }} // Add pointer cursor to indicate clickability
                  >
                    {renderThumbnailMedia(media, index)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering thumbnail click when removing
                        if (mode === "update") {
                          if (onRemoveImage) {
                            onRemoveImage(media);
                            removeImage(form, value, index);
                          } else {
                            removeImage(form, value, index);
                          }
                        } else {
                          removeImage(form, value, index);
                        }
                      }}
                      disabled={isProcessing}
                    >
                      <FiX size={28} />
                    </button>
                  </S.StyleThumbnail>
                ))}

                {/* Add icon thumbnail */}
                {value.length < maxFiles && !isProcessing && (
                  <S.StyleThumbnail size={size} onClick={handleClick}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "fit-content",
                        backgroundColor: "gray",
                        opacity: 0.5,
                        borderRadius: "10%",
                      }}
                    >
                      <FiPlus size={20} />
                    </div>
                  </S.StyleThumbnail>
                )}
              </S.StyleThumbnailWrapper>
            )}

            {/* Show active image indicator for multiple images */}
            {/* {multiple && value?.length > 1 && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "8px",
                  fontSize: "12px",
                  color: "#64748B",
                }}
              >
                Image {activeIndex + 1} of {value.length}
              </div>
            )} */}

            {/* Show error messages */}
            {(hasError || error) && (
              <ErrorMessage
                message={error || meta.error?.base64 || meta.error?.extension}
              />
            )}

            <S.StyleUploadInput
              type="file"
              ref={uploadInputRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleFileChange(form, event.target.files, value);
                event.target.value = "";
              }}
              accept={getAcceptType()}
              disabled={isProcessing || restProps.disabled}
              multiple={multiple}
            />
          </StyleFormWrapper>
        );
      }}
    </Field>
  );
};

export default Upload;
